// 💾 src/pages/auth/Register.tsx
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  createUserWithEmailAndPassword,
  updateProfile
} from "firebase/auth"
import { auth, db } from "../../services/firebase"
import { useTranslation } from "react-i18next"
import {
  doc,
  setDoc,
  serverTimestamp,
  collection,
  addDoc
} from "firebase/firestore"
import "./style/AuthPage.scss"

const Register = () => {
  const { lng = "en" } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (password !== confirmPassword) {
      setError(t("auth.register.passwordMismatch"))
      return
    }

    try {
      setLoading(true)

      // 1) Create user in Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      )
      const uid = userCredential.user.uid

      // 2) Set display name
      await updateProfile(userCredential.user, { displayName: fullName })

      // 3) Create Firestore profile with role & status
      await setDoc(doc(db, "users", uid), {
        uid,
        fullName,
        email,
        role: "client",            // ← default role
        status: "notVerified",    
        createdAt: serverTimestamp()
      })

      // 4) Initialize balance subcollection
      await setDoc(doc(db, "users", uid, "balance", "main"), {
        amount: 0,
        createdAt: serverTimestamp()
      })

      // 5) Send initial notification
      await addDoc(
        collection(db, "users", uid, "notifications"),
        {
          message: t("auth.register.welcomeNotification"),
          timestamp: serverTimestamp(),
          seen: "No"
        }
      )

      // 6) Redirect to login or verification
      navigate(`/${lng}`)
    } catch (err: any) {
      console.error("Registration error:", err)
      setError(err.message || t("auth.register.failed"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-info">
        <h2>{t("auth.login.slogan")}</h2>
        <div className="features">
          <div>
            <strong>160</strong>
            <span>{t("auth.login.countries")}</span>
          </div>
          <div>
            <strong>1961</strong>
            <span>{t("auth.login.tokens")}</span>
          </div>
          <div>
            <strong>36B+</strong>
            <span>{t("auth.login.volume")}</span>
          </div>
          <div>
            <strong>71M+</strong>
            <span>{t("auth.login.users")}</span>
          </div>
        </div>
      </div>

      <div className="auth-form">
        <form onSubmit={handleRegister}>
          <h2>{t("auth.register.title")}</h2>

          <input
            type="text"
            placeholder={t("auth.register.fullName")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />

          <input
            type="email"
            placeholder={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder={t("auth.register.confirmPassword")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          {error && <div className="error">{error}</div>}

          <button type="submit" disabled={loading}>
            {loading
              ? t("auth.register.loading")
              : t("auth.register.button")}
          </button>

          <div className="footer-link">
            {t("auth.register.haveAccount")}{" "}
            <span onClick={() => navigate(`/${lng}/login`)}>
              {t("auth.register.loginButton")}
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Register
